import type { HttpMethod } from '../../../../../types/web/http';
import { isPreFlightRequest } from '../../../cors/utils.ts';
import type { HandlerFunction, RouterFunction, ServerRequest, ServerRequestHeaders, ServerResponse } from './types.ts';
import { MediaType, sortBySpecificity } from '../../../../http/media-type.ts';
import getLogger from '../../../../logger.ts';
import { PathPatternParser } from '../../../util/pattern/path-pattern-parser.ts';
import type { MIMEType } from 'node:util';
import { isCompatibleWith } from '../../../../utils/mime-type-utils.ts';

export type RequestPredicate = {
    test(request: ServerRequest): boolean;
    and(other: RequestPredicate): RequestPredicate;
    or(other: RequestPredicate): RequestPredicate;
    negate(): RequestPredicate;
    toString(): string;
};

const logger = getLogger('web.async.fn.router');

export const REQUEST_ATTRIBUTE          = 'io.interop.web.async.fn.router.request';
export const URI_VARIABLES_ATTRIBUTE    = 'io.interop.web.async.fn.router.variables';
export const MATCHING_PATTERN_ATTRIBUTE = 'io.interop.web.async.fn.router.pattern';

function routerFunction<T extends ServerResponse>(route: RouterFunction<T>['route']): RouterFunction<T> {
    const result: RouterFunction<T> = {
        route: route,
        and: <T2 extends ServerResponse>(other: RouterFunction<T2>) => and(result, other)
    };
    return result;
}

function and<T1 extends ServerResponse, T2 extends ServerResponse>(first: RouterFunction<T1>, second: RouterFunction<T2>): RouterFunction<T1 | T2> {
    return routerFunction<T1 | T2>(async (request) => {
        const handler = first.route(request);
        if (handler !== undefined) {
            return handler;
        }
        return second.route(request);
    });
}

export function route<T extends ServerResponse>(predicate: RequestPredicate, handlerFunction: HandlerFunction<T>): RouterFunction<T> {

    return routerFunction<T>(async (request: ServerRequest) => {
        if (predicate.test(request)) {
            if (logger.enabledFor(`trace`)) {
                const logPrefix = request.exchange.logPrefix;
                logger.debug(`${logPrefix}Matched ${predicate}`);
            }
            return handlerFunction;
        }
        else {
            return; // undefined
        }
    });
}

export const predicates: {
    all: () => RequestPredicate;
    method: (method: HttpMethod) => RequestPredicate;
    methods: (...methods: HttpMethod[]) => RequestPredicate;
    path: (pattern: string) => RequestPredicate;
    pathPredicates: (patternParser: PathPatternParser)=> ((pattern: string) => RequestPredicate);
    headers: (predicate: (headers: ServerRequestHeaders) => boolean) => RequestPredicate;
    accept: (...mediaTypes: MIMEType[]) => RequestPredicate;
    GET: (pattern: string) => RequestPredicate;
    HEAD: (pattern: string) => RequestPredicate;
    POST: (pattern: string) => RequestPredicate;
    PUT: (pattern: string) => RequestPredicate;
    PATCH: (pattern: string) => RequestPredicate;
    DELETE: (pattern: string) => RequestPredicate;
    OPTIONS: (pattern: string) => RequestPredicate;
    queryParam: (name: string,  valueOrPredicate: string | ((value: string) => boolean)) => RequestPredicate;
} = function () {
    const logger = getLogger('request.predicates');

    function requestMethod(request: ServerRequest): HttpMethod {
        if (isPreFlightRequest(request.exchange.request)) {
            const accessControlRequestMethod = request.headers.firstHeader('Access-Control-Request-Method');
            if (accessControlRequestMethod !== undefined) {
                return accessControlRequestMethod.toUpperCase() as HttpMethod;
            }
        }
        return request.method;
    }

    function requestPredicate(predicate: (request: ServerRequest) => boolean): RequestPredicate {
        const result: RequestPredicate = {
            test: predicate,
            and: (other: RequestPredicate) => and(result, other),
            negate: () => negate(result),
            or: (other: RequestPredicate) => or(result, other),
            toString: () => predicate.toString(),
        };
        return result;
    }

    function and(first: RequestPredicate, other: RequestPredicate): RequestPredicate {
        return requestPredicate((request: ServerRequest) => first.test(request) && other.test(request));
    }

    function or(first: RequestPredicate, other: RequestPredicate): RequestPredicate {
        const orRequestPredicate = requestPredicate((request: ServerRequest) => first.test(request) || other.test(request));
        orRequestPredicate.toString = () => `(${first} || ${other})`;
        return orRequestPredicate;
    }
    function negate(delegate: RequestPredicate): RequestPredicate {
        const negateRequestPredicate = requestPredicate((request: ServerRequest) => !delegate.test(request));
        negateRequestPredicate.toString = () => `!${delegate}`;
        return negateRequestPredicate;
    }
    function method(httpMethod: HttpMethod): RequestPredicate {
        const singleHttpMethodPredicate = (request: ServerRequest) => {
            const method = requestMethod(request);
            const match = httpMethod === method;
            traceMatch('Method', httpMethod, method, match);
            return match;
        };
        singleHttpMethodPredicate.toString = () => `${httpMethod}`;
        return requestPredicate(singleHttpMethodPredicate)
    }
    function methods(...methods: HttpMethod[]): RequestPredicate {
        if (!methods) {
            throw new Error('At least one HTTP method must be provided');
        }
        if (methods.length === 1) {
            return method(methods[0]);
        }
        const httpMethods = new Set(...methods);
        const multipleHttpMethodsPredicate = (request: ServerRequest) => {
            const method = requestMethod(request);
            const match = httpMethods.has(method);
            traceMatch('Method', `[${[...httpMethods].join(', ')}]`, method, match);
            return match;
        }
        multipleHttpMethodsPredicate.toString = () => `[${methods.join(', ')}]`;
        return requestPredicate(multipleHttpMethodsPredicate);
    }
    function path(pattern: string): RequestPredicate {
        const parser = PathPatternParser.DEFAULT;
        pattern = parser.initFullPathPattern(pattern);
        return pathPredicates(parser)(pattern);
    }

    function pathPredicates(patternParser: PathPatternParser) {
        return (pattern: string): RequestPredicate => {
            const pathPattern = patternParser.parse(pattern);
            const pathPatternPredicate = (request: ServerRequest) => {
                const pathContainer = request.requestPath.pathWithinApplication;
                const info = pathPattern.matchAndExtract(pathContainer);
                traceMatch('Pattern', pathPattern.patternString, pathContainer.value, info !== undefined);
                if (info !== undefined) {

                    // todo modify attributes
                    return true;
                }
                else {
                    return false;
                }
            };
            pathPatternPredicate.toString = () => pathPattern.patternString;
            return requestPredicate(pathPatternPredicate);
        }
    }
    function traceMatch(prefix: string, desired: unknown, actual: unknown, match: boolean) {
        if (logger.enabledFor('trace')) {
            logger.debug(`${prefix} "${desired}" ${match ? 'matches' : 'does not match'} against value "${actual}"`);
        }
    }

    function headers(headersPredicate: (headers: ServerRequestHeaders) => boolean): RequestPredicate {
        const result = (request: ServerRequest) => {
            if (isPreFlightRequest(request.exchange.request)) {
                return true;
            }
            else {
                return headersPredicate(request.headers);
            }
        };
        result.toString = () => headersPredicate.toString();
        return requestPredicate(result);
    }

    function accept(...mediaTypes: MIMEType[]) {
        if (!mediaTypes) {
            throw new Error('mediaTypes must not be empty');
        }

        function acceptedMediaTypes(headers: ServerRequestHeaders) {
            let acceptedMediaTypes = headers.accept();
            if (acceptedMediaTypes.length === 0) {
                acceptedMediaTypes = [MediaType.ALL];
            }
            else {
                sortBySpecificity(acceptedMediaTypes);
            }
            return acceptedMediaTypes;
        }

        if (mediaTypes.length === 1) {
            const mediaType = mediaTypes[0];
            const singleAcceptPredicate = (headers: ServerRequestHeaders) => {
                const accepted = acceptedMediaTypes(headers);
                let match = false;
                for (const acceptedMediaType of accepted) {
                    if (isCompatibleWith(acceptedMediaType, mediaType)) {
                        match = true;
                        break;
                    }
                }
                traceMatch('Accept', mediaType, accepted, match);
                return match;
            };
            singleAcceptPredicate.toString = () => `Accept: ${mediaType}`;
            return headers(singleAcceptPredicate);
        }
        else {
            const multipleAcceptsPredicate = (headers: ServerRequestHeaders) => {
                const accepted = acceptedMediaTypes(headers);
                let match = false;
                outer:
                    for (const acceptedMediaType of accepted) {
                        for (const mediaType of mediaTypes) {
                            if (isCompatibleWith(acceptedMediaType, mediaType)) {
                                match = true;
                                break outer;
                            }
                        }
                    }
                traceMatch('Accept', mediaTypes, accepted, match);
                return match;
            }
            multipleAcceptsPredicate.toString = () => `Accept: ${mediaTypes.join(', ')}`;
            return headers(multipleAcceptsPredicate)
        }
    }

    function queryParam(name: string, valueOrPredicate: string | ((value: string) => boolean)): RequestPredicate {
        const value = typeof valueOrPredicate === 'string' ? valueOrPredicate : undefined;
        const predicate = typeof valueOrPredicate === 'string' ? (v: string) => v === value : valueOrPredicate;
        const queryParamPredicate = (request: ServerRequest) => {
            const s = request.queryParam(name);
            return s !== null && s !== undefined && predicate(s);
        };

        queryParamPredicate.toString = () => {
            return `?${name} ${valueOrPredicate}`;
        };
        return requestPredicate(queryParamPredicate);
    }


    return {
        all: () => requestPredicate((request) => true),
        method,
        methods,
        path,
        pathPredicates,
        headers,
        accept,
        GET: (pattern: string) => method('GET').and(path(pattern)),
        HEAD: (pattern: string) => method('HEAD').and(path(pattern)),
        POST: (pattern: string) => method('POST').and(path(pattern)),
        PUT: (pattern: string) => method('PUT').and(path(pattern)),
        PATCH: (pattern: string) => method('PATCH').and(path(pattern)),
        DELETE: (pattern: string) => method('DELETE').and(path(pattern)),
        OPTIONS: (pattern: string) => method('OPTIONS').and(path(pattern)),
        queryParam,
    }
}();
